import { all, call, fork, put, take } from "redux-saga/effects";

import { createApiClient, IApiClient } from "../../infra/api/client";

import { ClientBookmark } from "../../domain/model/client_bookmark";
import { Customer } from "../../domain/model/customer";
import { Occupation } from "../../domain/model/project";
import { IClientBookmarkRepository } from "../../domain/repository/client_bookmark_repository";
import {
  ICustomerRepository,
  TCustomerCountByOccupationRequest,
  TSearchCustomerRequest,
} from "../../domain/repository/customer_repository";
import {
  IJobCareerRepository,
  TJobCareerWithCustomerID,
} from "../../domain/repository/job_career_repository";
import { createClientBookmarkAPI } from "../../infra/api/service/client_bookmark_api";
import { createCustomerAPI } from "../../infra/api/service/customer_api";
import { createJobCareerAPI } from "../../infra/api/service/job_career_api";
import {
  ClientTopPageActionType,
  createClientTopPageActionCreator,
  IClientTopPageActionCreator,
  IRequestGetCustomersByClientTopPageAction,
} from "../action/client_top_page_action";
import { GetCustomersByClientTopPageResponse } from "../response/customer_response";
import { TOccupationCounts } from "../type/common";
import { handleErrorForHandler } from "./handleErrorForHandler";

const apiClient: IApiClient = createApiClient();
const customerRepository: ICustomerRepository = createCustomerAPI(apiClient);
const jobcareerRepository: IJobCareerRepository = createJobCareerAPI(apiClient);
const clientBookmarkRepository: IClientBookmarkRepository =
  createClientBookmarkAPI(apiClient);

const actionCreator: IClientTopPageActionCreator =
  createClientTopPageActionCreator();

function* handleGetCustomersInClientTopPage() {
  while (true) {
    try {
      const action: IRequestGetCustomersByClientTopPageAction = yield take(
        ClientTopPageActionType.REQUEST_GET_CUSTOMERS
      );
      const [customers, clientBookmarks] = yield all([
        call(getCustomers, action.item.searchCustomerRequest),
        call(getAllClientBookmark),
      ]);

      let occupationCounts: TOccupationCounts = {
        [Occupation.Occupation_CorporateBusinessPlanning]: 0,
        [Occupation.Occupation_BusinessDevelopment]: 0,
        [Occupation.Occupation_Sales]: 0,
        [Occupation.Occupation_PublicRelations]: 0,
        [Occupation.Occupation_IPO]: 0,
        [Occupation.Occupation_WebMarketing]: 0,
        [Occupation.Occupation_HumanResource]: 0,
        [Occupation.Occupation_Finance]: 0,
        [Occupation.Occupation_MinistryOfJustice]: 0,
        [Occupation.Occupation_Engineer]: 0,
        [Occupation.Occupation_InformationSystem]: 0,
        [Occupation.Occupation_Marketing]: 0,
      };
      const customerIdList: string[] = yield call(
        createCustomerIdList,
        customers
      );
      const jobCareersWithCustomerID: TJobCareerWithCustomerID[] = yield call(
        getJobcareerAllByCustomerIDs,
        customerIdList
      );

      const res: GetCustomersByClientTopPageResponse =
        new GetCustomersByClientTopPageResponse();
      res.customers = customers;
      res.jobCareersWithCustomerID = jobCareersWithCustomerID;
      res.clientBookmarks = clientBookmarks;
      res.occupationCounts = occupationCounts;

      yield put(
        actionCreator.callbackGetCustomersByClientTopPageAction(true, res)
      );

      const [
        Occupation_CorporateBusinessPlanning,
        Occupation_BusinessDevelopment,
        Occupation_Sales,
        Occupation_PublicRelations,
        Occupation_IPO,
        Occupation_WebMarketing,
        Occupation_HumanResource,
        Occupation_Finance,
        Occupation_MinistryOfJustice,
        Occupation_Engineer,
        Occupation_InformationSystem,
        Occupation_Marketing,
      ] = yield all([
        call(
          getOccupationCount,
          Occupation.Occupation_CorporateBusinessPlanning
        ),
        call(getOccupationCount, Occupation.Occupation_BusinessDevelopment),
        call(getOccupationCount, Occupation.Occupation_Sales),
        call(getOccupationCount, Occupation.Occupation_PublicRelations),
        call(getOccupationCount, Occupation.Occupation_IPO),
        call(getOccupationCount, Occupation.Occupation_WebMarketing),
        call(getOccupationCount, Occupation.Occupation_HumanResource),
        call(getOccupationCount, Occupation.Occupation_Finance),
        call(getOccupationCount, Occupation.Occupation_MinistryOfJustice),
        call(getOccupationCount, Occupation.Occupation_Engineer),
        call(getOccupationCount, Occupation.Occupation_InformationSystem),
        call(getOccupationCount, Occupation.Occupation_Marketing),
      ]);

      occupationCounts = {
        [Occupation.Occupation_CorporateBusinessPlanning]:
          Occupation_CorporateBusinessPlanning,
        [Occupation.Occupation_BusinessDevelopment]:
          Occupation_BusinessDevelopment,
        [Occupation.Occupation_Sales]: Occupation_Sales,
        [Occupation.Occupation_PublicRelations]: Occupation_PublicRelations,
        [Occupation.Occupation_IPO]: Occupation_IPO,
        [Occupation.Occupation_WebMarketing]: Occupation_WebMarketing,
        [Occupation.Occupation_HumanResource]: Occupation_HumanResource,
        [Occupation.Occupation_Finance]: Occupation_Finance,
        [Occupation.Occupation_MinistryOfJustice]: Occupation_MinistryOfJustice,
        [Occupation.Occupation_Engineer]: Occupation_Engineer,
        [Occupation.Occupation_InformationSystem]: Occupation_InformationSystem,
        [Occupation.Occupation_Marketing]: Occupation_Marketing,
      };
      res.occupationCounts = occupationCounts;

      yield put(
        actionCreator.callbackGetCustomersByClientTopPageAction(true, res)
      );
    } catch (error) {
      yield fork(handleErrorForHandler, error);
      yield put(
        actionCreator.callbackGetCustomersByClientTopPageAction(
          false,
          undefined
        )
      );
    }
  }
}

const createCustomerIdList = (customerList: Customer[]): string[] => {
  return customerList.map((item: Customer) => {
    return item.id;
  });
};

const getCustomers = (r: TSearchCustomerRequest) => {
  return customerRepository.getAllBySearch(
    r.query,
    r.singleSkillTagIDs,
    r.multiSkillTagIDs,
    r.occupationSkillTagCategory,
    r.occupationSkillTagId,
    r.gender,
    r.age,
    r.workIntention,
    r.fullTimePossibility,
    r.page,
    r.offset
  );
};

const getOccupationCount = (occupation: Occupation) => {
  const customerCountByOccupationRequest: TCustomerCountByOccupationRequest = {
    occupation,
  };
  return customerRepository.getCountByOccupation(
    customerCountByOccupationRequest
  );
};

const getAllClientBookmark = (): Promise<ClientBookmark[]> => {
  return clientBookmarkRepository.getAllByClient();
};

const getJobcareerAllByCustomerIDs = (
  ids: string[]
): Promise<TJobCareerWithCustomerID[]> => {
  return jobcareerRepository.getAllByCustomerIDsForScout(ids);
};

export { handleGetCustomersInClientTopPage };



// WEBPACK FOOTER //
// ./src/presentation/handler/client_top_page_handler.ts